import React from 'react';

/**
 * Msg #980 — Combat / Spell action pill bar.
 *
 * Extracted from ChatPage.jsx to shrink the mega-file. Pure presentational —
 * takes `npcsHere` (array of NPC objects in the caller's current room) and a
 * `onCommand(commandString)` callback. Renders the six action pills:
 *   ⚔ Attack {target}  — /attack {npc}
 *   🛡 Guard          — /me raises a guard, bracing for the next blow.
 *   🙏 Bless          — /bless
 *   🔮 Magic          — /magic {npc}          (1 MP + 1 HP recoil until Veteran, d20 ≥12 hits for 2 HP)
 *   ✨ Heal           — /heal                 (1 MP → +2 HP)
 */
export const ActionPillBar = ({ npcsHere = [], onCommand }) => {
  const monster = npcsHere.find((n) => n.isMonster);
  const attackTarget = monster || npcsHere[0];
  const runCmd = (cmd) => { if (onCommand) onCommand(cmd); };
  const pillStyle = (bg, disabled) => ({
    background: disabled ? '#c8c8b8' : bg,
    color: '#f5f5f0',
    border: '1px solid rgba(0,0,0,0.35)',
    padding: '3px 12px',
    fontSize: 12, fontWeight: 'bold',
    letterSpacing: 1, borderRadius: 16,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  });
  return (
    <div
      data-testid="action-pill-bar"
      className="mt-2 flex flex-wrap items-center gap-2"
      style={{
        padding: '5px 10px',
        border: '1px solid var(--room-border, #c8c8b8)',
        background: 'var(--room-bg, #fafaf7)',
        fontSize: 12,
      }}
    >
      <span style={{ fontWeight: 'bold', letterSpacing: 1, color: 'var(--room-text, #111)' }}>ACTION:</span>
      <button
        data-testid="pill-attack"
        disabled={!attackTarget}
        title={attackTarget ? `Attack ${attackTarget.name.replace('_', ' ')}` : 'No target in this room'}
        onClick={() => attackTarget && runCmd(`/attack ${attackTarget.name}`)}
        style={pillStyle('#8b1a1a', !attackTarget)}
      >⚔ Attack{attackTarget ? ` ${attackTarget.name.replace('_', ' ')}` : ''}</button>
      <button
        data-testid="pill-guard"
        title="Take a defensive stance this turn"
        onClick={() => runCmd('/me raises a guard, bracing for the next blow.')}
        style={pillStyle('#33456a', false)}
      >🛡 Guard</button>
      <button
        data-testid="pill-bless"
        title="Pray for Goddess_Sophetia's blessing (heals to full if she is in the room)"
        onClick={() => runCmd('/bless')}
        style={pillStyle('#7a5a1a', false)}
      >🙏 Bless</button>
      <button
        data-testid="pill-magic"
        disabled={!attackTarget}
        title={attackTarget
          ? `Cast Magic on ${attackTarget.name.replace('_', ' ')} (1 MP + 1 HP recoil until Veteran, d20 ≥12 for 2 HP)`
          : 'No target in this room'}
        onClick={() => attackTarget && runCmd(`/magic ${attackTarget.name}`)}
        style={pillStyle('#5a3a8a', !attackTarget)}
      >🔮 Magic</button>
      <button
        data-testid="pill-heal"
        title="Cast Heal on yourself (1 MP → +2 HP)"
        onClick={() => runCmd('/heal')}
        style={pillStyle('#2d8a5a', false)}
      >✨ Heal</button>
    </div>
  );
};

export default ActionPillBar;
